import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/UsageRecordsService';
import { UsageRecord } from '../types';

export const useUsageRecord = (usageRecordId?: string) => {
    const queryClient = useQueryClient();

    const usageRecordQuery = useQuery({
        queryKey: ['usage-record', usageRecordId],
        queryFn: () => fetch(usageRecordId!),
        enabled: !!usageRecordId
    });

    const createMutation = useMutation({
        mutationFn: (values: UsageRecord) => {
            const { created, updated, ...rest } = values;
            return add({ ...rest, created: new Date().toISOString() });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['usage-records'] });
        }
    });

    const updateMutation = useMutation({
        mutationFn: (values: UsageRecord) => {
            if (!usageRecordId) throw new Error('usageRecordId is required parameter');
            const { created, updated, ...rest } = values;
            return update(usageRecordId, { ...rest, created: created || new Date().toISOString() });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['usage-records'] });
            queryClient.invalidateQueries({ queryKey: ['usage-record', usageRecordId] });
        }
    });

    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!usageRecordId) throw new Error('usageRecordId is required parameter');
            return deleteRecord(usageRecordId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['usage-records'] });
        }
    });

    return {
        record: usageRecordQuery.data,
        loading: usageRecordQuery.isLoading,
        fetchRecord: usageRecordQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};